<template>
  <div class="fair-survey-step">
    <div class="fair-survey-step__head">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <div
      class="fair-survey-step__options"
      role="radiogroup"
      aria-label="Как часто носите каблук"
    >
      <button
        v-for="option in step.frequencyOptions"
        :key="option.value"
        type="button"
        role="radio"
        class="fair-survey-step__option"
        :class="{
          'fair-survey-step__option--selected': selectedFrequency === option.value,
        }"
        :aria-checked="selectedFrequency === option.value"
        @click="selectFrequency(option.value)"
      >
        <FairRadioButtonIndicator :selected="selectedFrequency === option.value" />
        <span class="fair-survey-step__option-label">{{ option.label }}</span>
      </button>
    </div>

    <!-- Heights only matter once heels are worn at all. -->
    <div
      v-if="showHeelSizes"
      class="heel-frequency-step__sizes"
    >
      <div class="fair-survey-step__head">
        <span class="fair-survey-step__eyebrow">Высота каблука</span>
        <p class="fair-survey-step__subtitle">
          Выберите все подходящие варианты
        </p>
      </div>

      <div class="fair-survey-step__grid">
        <button
          v-for="size in step.heelSizes"
          :key="size.value"
          type="button"
          class="fair-survey-step__card"
          :class="{
            'fair-survey-step__card--selected': isHeelSizeSelected(size.value),
          }"
          :aria-pressed="isHeelSizeSelected(size.value)"
          @click="toggleHeelSize(size.value)"
        >
          <img
            class="fair-survey-step__card-media"
            :src="getHeelSizeImage(size.value)"
            :alt="size.label"
          >
          <span class="fair-survey-step__card-label">{{ size.label }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useTasteClarificationModal } from '@/features/taste/store'
import type { TasteStep, HeelFrequencyValue, HeelSizeValue } from '@/features/taste/types/taste-config'
import { getHeelSizeImage } from '@/features/taste/utils/heel-size-images'
import { FairRadioButtonIndicator } from '@/ui/primitives/FairRadioButton'

interface Props {
  step: TasteStep & { type: 'heel_frequency' }
}

const props = defineProps<Props>()
const tasteStore = useTasteClarificationModal()

const selectedFrequency = ref<HeelFrequencyValue | null>(null)
const selectedHeelSizes = ref<HeelSizeValue[]>([])

const showHeelSizes = computed(() => {
  return selectedFrequency.value === 'sometimes' || selectedFrequency.value === 'often'
})

function selectFrequency(frequency: HeelFrequencyValue) {
  selectedFrequency.value = frequency
  tasteStore.updateTasteData(props.step.id, frequency)

  if (frequency === 'never') {
    selectedHeelSizes.value = []
    tasteStore.updateTasteData(`${props.step.id}_heelSizes`, [])
  }
}

function isHeelSizeSelected(size: HeelSizeValue): boolean {
  return selectedHeelSizes.value.includes(size)
}

function toggleHeelSize(size: HeelSizeValue) {
  selectedHeelSizes.value = isHeelSizeSelected(size)
    ? selectedHeelSizes.value.filter((candidate) => candidate !== size)
    : [...selectedHeelSizes.value, size]
}

watch(selectedHeelSizes, (newSizes) => {
  tasteStore.updateTasteData(`${props.step.id}_heelSizes`, newSizes)
})

onMounted(() => {
  selectedFrequency.value = tasteStore.tasteModal.tasteData[props.step.id] || null
  selectedHeelSizes.value = [...(tasteStore.tasteModal.tasteData[`${props.step.id}_heelSizes`] || [])] as HeelSizeValue[]
})
</script>
<style scoped>
.heel-frequency-step__sizes {
  display: flex;
  flex-direction: column;
  gap: var(--fair-rd-space-16);
}
</style>
